import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

const Choices = {
	PUBLIC_TEMPLATES: 'public_templates',
	SNAPSHOTS: 'snapshots',
	SOURCES: 'sources',
};

export default class WizardTourPopupWizardSecondComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	get isSnapshotsChoiceShown(): boolean {
		return !!this.mmBoardsService.normalBoardModels.length;
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const choices = [Choices.PUBLIC_TEMPLATES, Choices.SOURCES];
		if (this.isSnapshotsChoiceShown) {
			choices.push(Choices.SNAPSHOTS);
		}

		this.analyticsService.event(TrackingEvent.TEMPLATE_ONBOARDING_CHOICE_SHOWN, { choices });
	}

	@action
	choose(choice: string): void {
		this.analyticsService.event(TrackingEvent.TEMPLATE_ONBOARDING_CHOICE_COMPLETED, { choice });

		switch (choice) {
			case Choices.PUBLIC_TEMPLATES:
				// stays on public templates page
				break;
			case Choices.SNAPSHOTS: {
				this.onboardingService.triggerAutoScheduleSnapshot(); // stay on public templates page
				break;
			}
			case Choices.SOURCES:
				this.onboardingService.triggerWizardDataboardIntroTourModalOnSourcesChoiceSelected();
				break;
		}

		this.close();
	}

	@action
	skip() {
		this.analyticsService.event(TrackingEvent.TEMPLATE_ONBOARDING_CHOICE_SKIPPED);
		this.close();
	}
	<template>
		<div class="content">

			<div class="sides-ct has-wide-left-side">

				<div class="side side-left">

					<CommonUiIconComponent @name="Wizard" @class="wizard-icon" />
					{{! shown only when side-left is small }}

					<div class="ct">
						<img
							src="/images/wizards/first-left.png"
							width="200"
							height="160"
							alt="Wizard"
						/>

						<h2>What would you like to do next?</h2>

						<p>
							Congrats on successfully creating your first dashboard! But this is just
							the start…
						</p>
					</div>

				</div>

				<div class="side side-right">

					<h2>What would you like to do next?</h2>
					{{! shown only on mobile when side-left hidden }}

					<ul class="items-list items-list--selectable">

						<li
							role="button"
							class="item"
							{{on "click" (fn this.choose "public_templates")}}
						>
							<img
								src="/images/wizards/first-right-boards.png"
								width="63"
								height="63"
								alt
							/>
							<div class="t">Build a dashboard from another template</div>
							<p>
								Get your second dashboard started by browsing our template gallery
								with hundreds of dashboards ranging from marketing and sales, to
								project management.
							</p>
							<CommonUiIconComponent
								@name="ArrowRight"
								@size="md"
								@class="item-arrow"
							/>
						</li>

						{{#if this.isSnapshotsChoiceShown}}
							<li
								role="button"
								class="item"
								{{on "click" (fn this.choose "snapshots")}}
							>
								<img
									src="/images/wizards/second-right-snapshot.png"
									width="63"
									height="42"
									alt
								/>
								<div class="t">Schedule a snapshot by email</div>
								<p>
									Automate your reporting and get your new dashboard emailed
									straight to your inbox every day, week, or month to stay
									up-to-date with your performance metrics and progress.
								</p>
								<CommonUiIconComponent
									@name="ArrowRight"
									@size="md"
									@class="item-arrow"
								/>
							</li>
						{{/if}}

						<li role="button" class="item" {{on "click" (fn this.choose "sources")}}>
							<img
								class="source"
								src="/images/wizards/first-right-sources.png"
								width="93"
								height="80"
								alt
							/>
							<div class="t">Connect more data sources</div>
							<p>
								With Databox, you can visualize the data from all of your most
								critical business tools in one place. We've got 70+ one-click
								integrations ready for you to connect.
							</p>
							<CommonUiIconComponent
								@name="ArrowRight"
								@size="md"
								@class="item-arrow"
							/>
						</li>

					</ul>

					<a class="skip" role="button" {{on "click" this.skip}}>
						I would like to explore on my own
					</a>

				</div>

			</div>

		</div>
	</template>
}
